import { useFormContext, useWatch } from "react-hook-form"
import { useTranslation } from "react-i18next"
import { PencilSimple } from "@phosphor-icons/react"
import { DrawerWrapper } from "@/components/drawer-wrapper"
import { ScrollWrapper } from "@/components/scroll-wrapper"
import { Button } from "@/components/ui/button"
import { Card, CardBody, CardBodyContent, CardHeaderTitle } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Tag } from "@/components/ui/tag"
import { joinStrings } from "@/lib/joinString"
import { DiagnosisDrawer } from "@/modules/admin/treatment/components/sections/diagnosis/diagnosis-drawer"
import { TreatmentPlanTemplatesSectionCountryForm } from "@/modules/admin/treatment/components/sections/treatment-form-section-country"
import { TreatmentPlanTemplatesSectionInstructionForm } from "@/modules/admin/treatment/components/sections/treatment-form-section-instruction"
import { TreatmentPlanTemplatesSectionRecommendedForm } from "@/modules/admin/treatment/components/sections/treatment-form-section-recommened"
import { TreatmentPlanTemplatesSectionSynonymsForm } from "@/modules/admin/treatment/components/sections/treatment-form-section-synonyms"
import { TreatmentPlanTemplatesSectionTitleForm } from "@/modules/admin/treatment/components/sections/treatment-form-section-title"
import { TreatmentPlanTemplateSelector } from "@/modules/admin/treatment/components/treatment-plan-template-selector"
import { TreatmentForm } from "@/modules/admin/treatment/schema/treatment-form-schema"
import { useDrawerStore } from "@/stores/useDrawerStore"

const CreateEditTreatmentPlanTemplatesForm = () => {
  const { t } = useTranslation()
  const openDrawer = useDrawerStore((state) => state.openDrawer)

  const form = useFormContext<TreatmentForm>()

  const {
    register,
    formState: { errors: formErrors },
  } = form

  const diagnosisCodes = useWatch({ control: form.control, name: "diagnosisCodes" })

  return (
    <div className="bg-background-subdued flex items-center justify-between">
      <div className="flex h-full w-full">
        <ScrollWrapper className="h-full">
          <Card className="m-8" data-testid="treatment-plan-card">
            <div className="grid grid-cols-1 gap-2 border-b p-4">
              <CardHeaderTitle
                data-testid="treatment-plan-card-header-title"
                className="text-on-surface-subdued text-sm font-medium">
                {t("admin.treatment.treatmentPlan")}
              </CardHeaderTitle>
              <div className="grid grid-cols-2 items-center gap-2">
                <Input
                  {...register("name")}
                  testId="treatment-name"
                  label={t("admin.treatment.form.treatmentName")}
                  required
                  error={t(formErrors.name?.message ?? "", { max: 100 })}
                  errorClasses="truncate col-span-1"
                />
                <TreatmentPlanTemplateSelector />
              </div>
            </div>
            <CardBody>
              <CardBodyContent className="my-4 grid gap-4 border-b">
                <TreatmentPlanTemplatesSectionCountryForm />
              </CardBodyContent>
              <CardBodyContent className="my-4 grid gap-1 border-b">
                <>
                  <div className="flex w-full items-center justify-between">
                    <div className="text-on-surface-subdued col-span-4 text-sm font-medium">
                      <label>{t("admin.treatment.form.diagnosisCode")}</label>
                    </div>
                    <Button
                      data-testid="diagnosis-dialog-open-button"
                      variant="circle"
                      className="h-10 w-10"
                      onClick={() => {
                        openDrawer(() => <DiagnosisDrawer />, "DiagnosisDrawer")
                      }}>
                      <PencilSimple />
                    </Button>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {diagnosisCodes?.map((item) => (
                      <Tag key={item.id} label={joinStrings([item.diagnosisCode, item.text], " - ")} />
                    ))}
                  </div>
                </>
              </CardBodyContent>
              <CardBodyContent className="my-4 grid gap-4 border-b">
                <TreatmentPlanTemplatesSectionTitleForm />
              </CardBodyContent>
              <CardBodyContent className="my-4 grid gap-4 border-b">
                <TreatmentPlanTemplatesSectionSynonymsForm />
              </CardBodyContent>
              <CardBodyContent className="my-4 border-b">
                <TreatmentPlanTemplatesSectionRecommendedForm />
              </CardBodyContent>
              <CardBodyContent className="my-4">
                <TreatmentPlanTemplatesSectionInstructionForm />
              </CardBodyContent>
            </CardBody>
          </Card>
        </ScrollWrapper>
      </div>
      <DrawerWrapper containerId="diagnosis-drawer" />
    </div>
  )
}

export { CreateEditTreatmentPlanTemplatesForm }
